import { useEffect, useRef, useState } from 'react';
import type { NavigateFunction } from 'react-router-dom';

import type { AddressForm } from 'features/conversation-preferences/services/conversation-preferences-service';
import { useChatStore } from 'state/store/chat/use-chat-store';
import {
  cancelPendingOnboardingAnswer,
  hydrateOnboardingConversation,
  onboardingQuestionFor,
  relocalizeOnboardingQuestion,
} from '../onboarding-chat';
import { onboardingIndustryLabel } from '../onboarding-industry';
import {
  markOnboardingCompletedHere,
  markOnboardingStepFinished,
  ONBOARDING_TOTAL_STEPS,
  skipOnboardingCatchUp,
  wasOnboardingCompletedHere,
  type OnboardingAnswers,
} from '../onboarding-journey';
import { onboardingStartCreatingPrompt } from '../onboarding-script';
import { generateOnboardingStarterPrompt } from '../onboarding-starter';

export const progressOf = (answers: OnboardingAnswers) => ({
  ...answers,
  name: answers.onboardingName,
  team: answers.onboardingTeam,
  tools: answers.onboardingTools,
  completed: answers.onboardingCompleted,
});

/** Stale localStorage can leave completed=true without tools — require both for the done card.
 *  A catch-up starts from a finished old journey, so it is done once its industry is answered. */
export const isJourneyDone = (answers: OnboardingAnswers) =>
  answers.catchUp
    ? Boolean(answers.industry)
    : Boolean(answers.onboardingCompleted && answers.onboardingTools?.trim());

/** Keeps the local transcript in step with the answers: rebuilt after a reload, the current
 *  question re-rendered after a language switch, and a pending answer dropped on leaving. */
export function useOnboardingTranscript(
  conversationId: string | undefined,
  step: number | null,
  answers: OnboardingAnswers,
  language: string | undefined,
  userId: string | undefined
) {
  const shownLanguage = useRef(language);
  const {
    onboardingName,
    onboardingTeam,
    onboardingTools,
    addressForm: answeredForm,
    familyName,
    salutation,
    industry,
    industryText,
    catchUp,
  } = answers;

  useEffect(() => {
    if (!conversationId) return;
    hydrateOnboardingConversation(conversationId, {
      onboardingName,
      onboardingTeam,
      onboardingTools,
      addressForm: answeredForm,
      familyName,
      salutation,
      industry,
      industryText,
      catchUp,
    });
  }, [
    conversationId,
    onboardingName,
    onboardingTeam,
    onboardingTools,
    answeredForm,
    familyName,
    salutation,
    industry,
    industryText,
    catchUp,
  ]);

  // A language switch re-renders the current question in the new language; answers are kept.
  useEffect(() => {
    if (shownLanguage.current === language) return;
    shownLanguage.current = language;
    if (conversationId && step) {
      relocalizeOnboardingQuestion(
        conversationId,
        onboardingQuestionFor(step, progressOf(answers))
      );
    }
  }, [language, conversationId, step, answers]);

  useEffect(() => {
    return () => {
      if (conversationId) cancelPendingOnboardingAnswer(conversationId);
    };
  }, [conversationId, step, userId]);
}

/**
 * Finished on another tab or in the Klax app: nothing is left to ask here (C12). The chat leaves
 * the journey too, or the composer would keep answering onboarding over that result. This tab's
 * own completion also writes v2; it is remembered, so changing an answer afterwards reopens the
 * step instead of reading as finished elsewhere.
 */
export function useJourneyExit({
  isOnboarding,
  isDone,
  isDoneElsewhere,
  conversationId,
  navigate,
}: {
  isOnboarding: boolean;
  isDone: boolean;
  isDoneElsewhere: boolean;
  conversationId: string | undefined;
  navigate: NavigateFunction;
}) {
  useEffect(() => {
    if (isOnboarding && isDone && conversationId) markOnboardingCompletedHere(conversationId);
  }, [isOnboarding, isDone, conversationId]);
  const leavesJourney =
    isOnboarding && isDoneElsewhere && !isDone && !wasOnboardingCompletedHere(conversationId);
  useEffect(() => {
    if (!leavesJourney) return;
    useChatStore.getState().setActiveConversationId(null);
    navigate('/chat', { replace: true });
  }, [leavesJourney, navigate]);
  return leavesJourney;
}

/** Leaving the journey: finished (optionally with a starter prompt) or the catch-up skipped. */
export function useJourneyFinish({
  conversationId,
  answers,
  userId,
  navigate,
}: {
  conversationId: string | undefined;
  answers: OnboardingAnswers;
  userId: string | undefined;
  navigate: NavigateFunction;
}) {
  const finishOnboarding = (prompt?: string) => {
    if (userId) {
      markOnboardingStepFinished(userId, ONBOARDING_TOTAL_STEPS, conversationId, {
        ...answers,
        onboardingCompleted: true,
        catchUp: false,
      });
    }
    useChatStore.getState().setActiveConversationId(null);
    navigate('/chat', {
      replace: true,
      ...(prompt ? { state: { prompt, agentDraft: prompt } } : {}),
    });
  };

  const skipCatchUp = () => {
    if (userId) skipOnboardingCatchUp(userId);
    useChatStore.getState().setActiveConversationId(null);
    navigate('/chat', { replace: true });
  };

  return { finishOnboarding, skipCatchUp };
}

type StarterChatOptions = {
  disabled?: boolean;
  language: string | undefined;
  addressForm: AddressForm;
  answers: OnboardingAnswers;
  finishOnboarding: (prompt?: string) => void;
  /** A change of any of these drops a starter request still running. */
  conversationId: string | undefined;
  step: number | null;
  userId: string | undefined;
  isOnboarding: boolean;
};

/** [Start Creating]: asks for a starter prompt (one at a time) and finishes with it, or with the
 *  written fallback when the request fails. */
export function useStarterChat({
  disabled,
  language,
  addressForm,
  answers,
  finishOnboarding,
  conversationId,
  step,
  userId,
  isOnboarding,
}: StarterChatOptions) {
  const [isPreparingChat, setIsPreparingChat] = useState(false);
  const starterRequest = useRef<AbortController | null>(null);

  useEffect(() => {
    setIsPreparingChat(false);
    return () => {
      starterRequest.current?.abort();
      starterRequest.current = null;
    };
  }, [
    conversationId,
    step,
    userId,
    isOnboarding,
    answers.onboardingCompleted,
    answers.onboardingTeam,
    answers.onboardingTools,
  ]);

  const startChat = async () => {
    if (disabled || starterRequest.current) return;
    const starterAnswers = {
      onboardingTeam: answers.onboardingTeam,
      onboardingTools: answers.onboardingTools,
      onboardingIndustry: onboardingIndustryLabel(answers.industry, answers.industryText),
    };
    const controller = new AbortController();
    starterRequest.current = controller;
    setIsPreparingChat(true);
    try {
      const prompt = await generateOnboardingStarterPrompt(
        { ...starterAnswers, language, addressForm },
        controller.signal
      );
      if (!controller.signal.aborted) finishOnboarding(prompt);
    } catch {
      if (!controller.signal.aborted)
        finishOnboarding(onboardingStartCreatingPrompt(starterAnswers, addressForm));
    } finally {
      if (starterRequest.current === controller) {
        starterRequest.current = null;
        setIsPreparingChat(false);
      }
    }
  };

  return { isPreparingChat, startChat: () => void startChat() };
}
